// Small drawing helpers shared by the creature, critter, glyph and particle code.
// All shapes are drawn with PixiJS v8 Graphics in code: flat fills, bold INK outlines.
import type { Graphics } from "pixi.js";
import { INK } from "../palette";

/** Outlined ellipse: flat fill + bold ink stroke. */
export function oval(g: Graphics, x: number, y: number, rx: number, ry: number, fill: number, ow: number): Graphics {
  return g.ellipse(x, y, rx, ry).fill({ color: fill }).stroke({ width: ow, color: INK });
}

/** Outlined circle. */
export function disc(g: Graphics, x: number, y: number, r: number, fill: number, ow: number): Graphics {
  return g.circle(x, y, r).fill({ color: fill }).stroke({ width: ow, color: INK });
}

/** Plain (un-outlined) ellipse. */
export function blot(g: Graphics, x: number, y: number, rx: number, ry: number, fill: number, alpha = 1): Graphics {
  return g.ellipse(x, y, rx, ry).fill({ color: fill, alpha });
}

/** Trace a polyline path given as flat [x0,y0,x1,y1,...] (no fill/stroke yet). */
function trace(g: Graphics, pts: readonly number[]): void {
  g.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]);
}

/** Trace a smooth curve through flat points using quadratic segments via midpoints. */
export function traceSmooth(g: Graphics, pts: readonly number[], closed: boolean): void {
  const n = pts.length / 2;
  if (n < 3) {
    trace(g, pts);
    return;
  }
  if (closed) {
    const mx = (pts[(n - 1) * 2] + pts[0]) / 2,
      my = (pts[(n - 1) * 2 + 1] + pts[1]) / 2;
    g.moveTo(mx, my);
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const x = pts[i * 2],
        y = pts[i * 2 + 1];
      g.quadraticCurveTo(x, y, (x + pts[j * 2]) / 2, (y + pts[j * 2 + 1]) / 2);
    }
    g.closePath();
  } else {
    g.moveTo(pts[0], pts[1]);
    for (let i = 1; i < n - 1; i++) {
      const x = pts[i * 2],
        y = pts[i * 2 + 1];
      g.quadraticCurveTo(x, y, (x + pts[i * 2 + 2]) / 2, (y + pts[i * 2 + 3]) / 2);
    }
    g.lineTo(pts[(n - 1) * 2], pts[(n - 1) * 2 + 1]);
  }
}

/**
 * Outlined thick line (a "noodle"): an ink stroke `width + 2*ow` with a colored stroke on top.
 * Used for legs, antennae, stalks, tendrils. `smooth` traces quadratic curves through the points.
 */
export function noodle(
  g: Graphics,
  pts: readonly number[],
  width: number,
  color: number,
  ow: number,
  smooth = false,
): Graphics {
  const t = smooth ? (p: readonly number[]) => traceSmooth(g, p, false) : (p: readonly number[]) => trace(g, p);
  t(pts);
  g.stroke({ width: width + ow * 2, color: INK, cap: "round", join: "round" });
  t(pts);
  g.stroke({ width, color, cap: "round", join: "round" });
  return g;
}

/** A plain stroked ink line (smile, brows, closed eyes). */
export function inkLine(g: Graphics, pts: readonly number[], width: number, smooth = false, color = INK): Graphics {
  if (smooth) traceSmooth(g, pts, false);
  else trace(g, pts);
  return g.stroke({ width, color, cap: "round", join: "round" });
}

/** Outlined closed polygon (optionally smoothed). */
export function blob(g: Graphics, pts: readonly number[], fill: number, ow: number, smooth = true): Graphics {
  if (smooth) traceSmooth(g, pts, true);
  else {
    trace(g, pts);
    g.closePath();
  }
  return g.fill({ color: fill }).stroke({ width: ow, color: INK, join: "round" });
}

/** Trace an upper half-disc (dome) of radius r sitting on the line y = cy (closed path, not filled). */
export function traceDome(g: Graphics, cx: number, cy: number, rx: number, ry = rx, steps = 18): Graphics {
  g.moveTo(cx - rx, cy);
  for (let i = 1; i <= steps; i++) {
    const a = Math.PI + (i / steps) * Math.PI;
    g.lineTo(cx + Math.cos(a) * rx, cy + Math.sin(a) * ry);
  }
  return g.closePath();
}

/** Half-width of an ellipse (rx, ry) at vertical offset dy from its centre (0 outside). */
export function chord(rx: number, ry: number, dy: number): number {
  const k = 1 - (dy * dy) / (ry * ry);
  return k <= 0 ? 0 : rx * Math.sqrt(k);
}

export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}
